import type { MetricRecord } from "@/shared/domain/metrics";

export interface MetricTreeNode {
  /** Last dotted segment of the metric name (e.g. "x" for "imu.accel.x"). */
  label: string;
  /** Full dotted path from the root. Unique within a tree, used as a stable key. */
  path: string;
  /** 0-based depth from the root. */
  depth: number;
  /** Direct children, sorted by label with numeric-aware locale compare. */
  children: MetricTreeNode[];
  /** Records whose `name` matches this node's exact `path`. Branches that are
   *  not themselves published metrics carry an empty array. */
  records: MetricRecord[];
  /** Total records in the entire subtree rooted at this node. */
  recordCount: number;
}

/**
 * Build a forest of metric trees from a flat list of `MetricRecord`s by
 * splitting each record's `name` on `.`.
 *
 * Behaviour:
 *  - One leaf per unique metric name. Multiple records sharing a name are
 *    folded into the same leaf's `records` array.
 *  - Intermediate dotted segments become branch nodes with empty `records`.
 *  - A name that is *both* a parent and a leaf (e.g. publishing both `imu`
 *    and `imu.accel`) attaches its records to the parent node — that node's
 *    `records` is non-empty *and* `children` is non-empty.
 *  - Siblings at every level are sorted by `label.localeCompare` with the
 *    `numeric` collation option so `x2` precedes `x10`.
 */
export function buildMetricTree(records: MetricRecord[]): MetricTreeNode[] {
  const roots: MetricTreeNode[] = [];
  const byPath = new Map<string, MetricTreeNode>();

  for (const record of records) {
    const segments = record.name.split(".");
    let parentChildren = roots;
    let path = "";
    for (let depth = 0; depth < segments.length; depth++) {
      const segment = segments[depth];
      path = path === "" ? segment : `${path}.${segment}`;
      let node = byPath.get(path);
      if (node === undefined) {
        node = {
          label: segment,
          path,
          depth,
          children: [],
          records: [],
          recordCount: 0,
        };
        byPath.set(path, node);
        parentChildren.push(node);
      }
      if (depth === segments.length - 1) {
        node.records.push(record);
      }
      parentChildren = node.children;
    }
  }

  sortAndCount(roots);
  return roots;
}

/**
 * Depth-first search for the node whose `path` equals `path`, across the whole
 * forest. Selection state holds a path (not a node reference) so it survives a
 * `buildMetricTree` rebuild on data refetch; this resolves it back to the live
 * node. Returns `undefined` when no node matches (e.g. the selected metric was
 * removed by a refetch).
 */
export function findNodeByPath(
  nodes: MetricTreeNode[],
  path: string,
): MetricTreeNode | undefined {
  for (const node of nodes) {
    if (node.path === path) {
      return node;
    }
    const found = findNodeByPath(node.children, path);
    if (found !== undefined) {
      return found;
    }
  }
  return undefined;
}

/**
 * Every dotted prefix of `path`, root-first, excluding `path` itself — i.e. the
 * branches that must be expanded to reveal the node at `path`. `"imu.accel.x"`
 * yields `["imu", "imu.accel"]`; an un-dotted path yields `[]`.
 */
export function ancestorPaths(path: string): string[] {
  const segments = path.split(".");
  const ancestors: string[] = [];
  for (let i = 1; i < segments.length; i++) {
    ancestors.push(segments.slice(0, i).join("."));
  }
  return ancestors;
}

/**
 * The most recently published record in `records`, or `undefined` when empty.
 * A leaf folds every record sharing its name; the table and the detail card
 * both surface this single newest record's value, timestamp, and attribution.
 */
export function latestRecord(
  records: MetricRecord[],
): MetricRecord | undefined {
  if (records.length === 0) {
    return undefined;
  }
  let best = records[0];
  for (let i = 1; i < records.length; i++) {
    if (records[i].published > best.published) {
      best = records[i];
    }
  }
  return best;
}

function sortAndCount(nodes: MetricTreeNode[]): number {
  nodes.sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { numeric: true }),
  );
  let total = 0;
  for (const node of nodes) {
    node.recordCount = node.records.length + sortAndCount(node.children);
    total += node.recordCount;
  }
  return total;
}
